import React, { useMemo, useContext, memo, useEffect, useRef, useCallback } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import AuthContext from './auth/Context';

import ReportFrame from './ReportFrame';
import { adaptLegacyReportConfig } from './DataDynamicPage';

// ============================================================================
// WIDGET HIỂN THỊ MỘT REPORT TRONG DASHBOARD
// Cấu hình report được lưu ở API "reportinfo" (tra cứu theo "code").
// Cùng cơ chế hiển thị như trang /page/report/:code (xem DataDynamicPage.jsx),
// nhưng bật cờ isWidget để nhúng gọn vào ô dashboard (ẩn header, nền trong suốt).
// ============================================================================
const DashboardReportWidget = memo(({ code, condition, refreshTrigger, frameRef }) => {
  const context = useContext(AuthContext);
  const userToken = context.userInfo?.token || (typeof context.getToken === 'function' ? context.getToken() : '');

  const reportRef = useRef(null);
  // Chuyển ref của ReportFrame ra ngoài (widget dashboard đọc điều kiện đang hiện để mở trang báo cáo gốc).
  const ganRef = useCallback((r) => {
    reportRef.current = r;
    if (typeof frameRef === 'function') frameRef(r);
    else if (frameRef) frameRef.current = r;
  }, [frameRef]);

  // Tự làm mới nền trong khoảng 5–10 phút để tránh dồn tải đồng loạt
  const autoRefreshInterval = useMemo(() => {
    const minMs = 5 * 60 * 1000;
    const maxMs = 10 * 60 * 1000;
    return Math.floor(Math.random() * (maxMs - minMs + 1) + minMs);
  }, []);

  // Ưu tiên cấu hình local trong context (nếu core đã nhúng sẵn), nếu không fetch server
  const localConfig = useMemo(
    () => (context.config?.getReportConfig ? context.config.getReportConfig(code) : null),
    [context.config, code]
  );
  const { data: fetchedConfig, isLoading, error } = useQuery({
    queryKey: ['reportInfo_widget', context.appInfo?._id, code],
    queryFn: async () => {
      // 1) ƯU TIÊN model MỚI `reportconfig` — đã là ĐỊNH DẠNG MỚI (displayFields/filterFields/…)
      //    nên dùng THẲNG, KHÔNG cần adaptLegacyReportConfig.
      try {
        const resNew = await context.apis.asyncGetList(userToken, "reportconfig", { condition: { code }, limit: 1 });
        const itemNew = resNew?.data?.[0] || resNew?.[0];
        if (itemNew) {
          return itemNew.toObject ? itemNew.toObject() : (itemNew._doc || itemNew);
        }
      } catch (e) {
        // reportconfig có thể chưa tồn tại ở một số hệ thống -> bỏ qua, fallback reportinfo
        console.warn("[DataDynamicPage] reportconfig không khả dụng, fallback reportinfo:", e?.message || e);
      }
      // 2) FALLBACK model CŨ `reportinfo` — nếu là bản legacy (`columns` mà chưa có `displayFields`)
      //    thì dịch sang định dạng mới
      const res = await context.apis.asyncGetList(userToken, 'reportinfo', { condition: { code }, limit: 1 });
      let item = res?.data?.[0] || res?.[0];
      if (!item) throw new Error('Không tìm thấy cấu hình báo cáo');
      // Cấu hình cũ (legacy) chỉ có "columns" mà chưa có "displayFields" → dịch sang định dạng mới
      if (item.columns && !item.displayFields) {
        item = await adaptLegacyReportConfig(item, context);
      }
      return item;
    },
    enabled: !localConfig && !!code && !!userToken,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const reportConfig = localConfig || fetchedConfig;

  // Nút "Làm mới" trên header widget tăng refreshTrigger → gọi refetch trong ReportFrame
  useEffect(() => {
    if (refreshTrigger) reportRef.current?.refresh();
  }, [refreshTrigger]);

  if (!localConfig && isLoading) {
    return <Box sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}><CircularProgress /></Box>;
  }
  if (error) {
    return <Box sx={{ p: 2, color: 'error.main' }}><Typography variant="body2">Lỗi tải Báo cáo: {error.message}</Typography></Box>;
  }
  if (!reportConfig) {
    return <Box sx={{ p: 2, color: 'text.secondary' }}><Typography variant="body2">Không tìm thấy cấu hình báo cáo "{code}"</Typography></Box>;
  }

  const mergedCondition = {
    ...(reportConfig.defaultCondition || {}),
    ...(reportConfig.reportCondition || {}),
    ...(condition || {}),
  };

  return (
    <ReportFrame
      ref={ganRef}
      isWidget={true}
      {...reportConfig}
      code={reportConfig.code || code}
      defaultCondition={mergedCondition}
      enableLoadMore={false}
      refreshInterval={autoRefreshInterval}
      contentSx={{ p: 0 }}
      containerSx={{ height: '100%' }}
    />
  );
});

export default DashboardReportWidget;
